import { type FC, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import type { MetaAdsConnectionStatus } from "shared";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import {
	useMetaAdsAdminCheck,
	useMetaAdsAdminConnections,
} from "./useMetaAdsAdmin";

const formatStatus = (status: MetaAdsConnectionStatus) => {
	switch (status) {
		case "connected":
			return "Connected";
		case "requires_reconnection":
			return "Requires Reconnection";
		default:
			return "Disconnected";
	}
};

const statusClass = (status: MetaAdsConnectionStatus) => {
	switch (status) {
		case "connected":
			return "bg-success";
		case "requires_reconnection":
			return "bg-warning text-dark";
		default:
			return "bg-secondary";
	}
};

export const PageMetaAdsAdminConnections: FC = () => {
	const { data: adminCheck, isLoading: checkLoading } = useMetaAdsAdminCheck();
	const { data: connections = [], isLoading } = useMetaAdsAdminConnections(
		adminCheck?.isAdmin === true,
	);
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<
		"all" | MetaAdsConnectionStatus
	>("all");

	const filtered = useMemo(() => {
		const q = search.toLowerCase();
		return connections.filter((item) => {
			const matchesSearch =
				item.companyName.toLowerCase().includes(q) ||
				(item.adAccountName ?? "").toLowerCase().includes(q) ||
				(item.adAccountId ?? "").toLowerCase().includes(q);
			const matchesStatus =
				statusFilter === "all" || item.connectionStatus === statusFilter;
			return matchesSearch && matchesStatus;
		});
	}, [connections, search, statusFilter]);

	if (checkLoading) {
		return (
			<AppPage title="Meta Ads Admin">
				<p>Loading...</p>
			</AppPage>
		);
	}

	if (!adminCheck?.isAdmin) {
		return <Navigate to={LinkGen.MetaAds.Index()} replace />;
	}

	return (
		<AppPage title="Meta Ads Connection Health">
			<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
				<p className="text-muted" style={{ marginBottom: 0 }}>
					Platform-wide view of Meta Ads connection status across all clients.
				</p>

				<div
					style={{
						display: "flex",
						gap: 12,
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					<input
						type="search"
						className="form-control"
						style={{ maxWidth: 320 }}
						placeholder="Search company or ad account..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					<Select
						className="form-select"
						style={{ maxWidth: 220 }}
						value={statusFilter}
						onChange={(e) =>
							setStatusFilter(e.target.value as "all" | MetaAdsConnectionStatus)
						}
					>
						<option value="all">All statuses</option>
						<option value="connected">Connected</option>
						<option value="requires_reconnection">Requires Reconnection</option>
						<option value="disconnected">Disconnected</option>
					</Select>
				</div>

				<Card>
					<Card.Header>Connections ({filtered.length})</Card.Header>
					<Card.Body style={{ padding: 0, overflowX: "auto" }}>
						{isLoading ? (
							<p style={{ padding: 16, margin: 0 }}>Loading connections...</p>
						) : (
							<table className="table" style={{ marginBottom: 0 }}>
								<thead>
									<tr>
										<th>Company</th>
										<th>Status</th>
										<th>Ad Account</th>
										<th>Ad Account ID</th>
										<th>Last Checked</th>
									</tr>
								</thead>
								<tbody>
									{filtered.length === 0 && (
										<tr>
											<td colSpan={5} className="text-muted text-center">
												No connections match your filters.
											</td>
										</tr>
									)}
									{filtered.map((item) => (
										<tr key={item.companyId}>
											<td>{item.companyName}</td>
											<td>
												<span
													className={`badge ${statusClass(item.connectionStatus)}`}
												>
													{formatStatus(item.connectionStatus)}
												</span>
											</td>
											<td>{item.adAccountName ?? "-"}</td>
											<td>
												<code>{item.adAccountId ?? "-"}</code>
											</td>
											<td>
												{item.lastChecked
													? new Date(item.lastChecked).toLocaleString()
													: "-"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</Card.Body>
				</Card>
			</div>
		</AppPage>
	);
};
